import React, { useState } from "react";
import { Table, Spinner } from "react-bootstrap";
import CountryList from "../../CountryList";
import { appleApi } from "../../../services/api";
import { FaChartPie, FaSearch, FaFileDownload } from "react-icons/fa";
import {
  LineChart, Line, BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip, Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

const KPIData = () => {
  const [appName, setAppName] = useState("");
  const [country, setCountry] = useState("");
  const [appDetails, setAppDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchAppDetails = async () => {
    try {
      setLoading(true);
      const { data: result } = await appleApi.get("analytics-and-reporting/", {
                                params: {
                                  app_name: appName,
                                  country_code: country,
                                },
                              });

      setAppDetails(result ?? {}); // Directly set the response data to appDetails
      setError(null);
    } catch (error) {
      console.error("Error fetching app details:", error);
      setError("Failed to fetch app details");
      setAppDetails({});
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchAppDetails(); // Fetch data on form submission
  };

  const downloadCSV = () => {
    if (!appDetails) return; // Ensure data is available before downloading

    const csvContent = `data:text/csv;charset=utf-8,${[
      "Total Installs,Estimated Uninstall Rate (%),Average Rating,Total Reviews,Daily Active Users,Monthly Active Users,Retention Rate (%),Churn Rate (%),Avg Session Duration,User Acquisition Cost,Lifetime Value,Average Revenue Per User,Total Reviews (KPI),Average Score",
      `${appDetails["Total Installs"]},${appDetails[
        "Estimated Uninstall Rate (rough)"
      ].toFixed(2)},${appDetails["Average Rating"].toFixed(2)},${appDetails[
        "Total Reviews"
      ].toLocaleString()},${appDetails.DAU},${appDetails.MAU},${
        appDetails.retention_rate
      },${appDetails.churn_rate},${appDetails.avg_session_duration},${
        appDetails.UAC
      },${appDetails.LTV},${appDetails.ARPU},${appDetails.userRatingCount},${
        appDetails.averageUserRating
      }`,
    ].join("\n")}`;

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "analytics_report.csv");
    document.body.appendChild(link);

    link.click();
    document.body.removeChild(link);
  };

  const downloadPDF = async () => {
    if (!appDetails) return; // Ensure data is available before downloading PDF

    const doc = new jsPDF();
    doc.text("Analytics Report", 14, 16);

    autoTable(doc, {
      head: [
        [
          "Total Installs",
          "Estimated Uninstall Rate (%)",
          "Average Rating",
          "Total Reviews",
        ],
      ],
      body: [
        [
          appDetails["Total Installs"],
          appDetails["Estimated Uninstall Rate (rough)"].toFixed(2),
          appDetails["Average Rating"].toFixed(2),
          appDetails["Total Reviews"].toLocaleString(),
        ],
      ],
      startY: 30,
    });

    autoTable(doc, {
      head: [
        [
          "Daily Active Users",
          "Monthly Active Users",
          "Retention Rate (%)",
          "Churn Rate (%)",
          "Avg Session Duration",
          "User Acquisition Cost",
          "Lifetime Value",
          "Average Revenue Per User",
          "Total Reviews (KPI)",
          "Average Score",
        ],
      ],
      body: [
        [
          appDetails.DAU,
          appDetails.MAU,
          appDetails.retention_rate,
          appDetails.churn_rate,
          appDetails.avg_session_duration,
          appDetails.UAC,
          appDetails.LTV,
          appDetails.ARPU,
          appDetails.userRatingCount,
          appDetails.averageUserRating,
        ],
      ],
      startY: doc.previousAutoTable.finalY + 10,
    });

    const chartContainer1 = document.querySelector("#uninstallRateChart");
    const chartContainer2 = document.querySelector("#averageRatingChart");

    if (chartContainer1 && chartContainer2) {
      const canvas1 = await html2canvas(chartContainer1);
      const imgData1 = canvas1.toDataURL("image/png");
      const canvas2 = await html2canvas(chartContainer2);
      const imgData2 = canvas2.toDataURL("image/png");

      const imgWidth = 180;
      const imgHeight1 = (canvas1.height * imgWidth) / canvas1.width;
      const imgHeight2 = (canvas2.height * imgWidth) / canvas2.width;

      doc.addPage();
      doc.text("Uninstall Rate Chart", 14, 16);
      doc.addImage(imgData1, "PNG", 14, 30, imgWidth, imgHeight1);

      doc.addPage();
      doc.text("Average Rating Chart", 14, 16);
      doc.addImage(imgData2, "PNG", 14, 30, imgWidth, imgHeight2);
    }

    doc.save("analytics_report.pdf");
  };
  const data = [
    {
      name: "Metrics",
      "Daily Active Users": appDetails?.DAU || 0,
      "Monthly Active Users": appDetails?.MAU || 0,
      "Retention Rate": appDetails?.retention_rate || 0,
      "Churn Rate": appDetails?.churn_rate || 0,
      "Avg Session Duration": appDetails?.avg_session_duration || 0,
      "User Acquisition Cost": appDetails?.UAC || 0,
      "Lifetime Value": appDetails?.LTV || 0,
      "Average Revenue Per User": appDetails?.ARPU || 0,
      "Total Reviews": appDetails?.userRatingCount || 0,
      "Average Score": appDetails?.averageUserRating || 0,
    },
  ];
  

  const lineColors = {
    "Daily Active Users": "#8884d8",
    "Monthly Active Users": "#82ca9d",
    "Retention Rate": "#ffc658",
    "Churn Rate": "#ff7300",
    "Avg Session Duration": "#387908",
    "User Acquisition Cost": "#b82500",
    "Lifetime Value": "#00b894",
    "Average Revenue Per User": "#6c63ff",
    "Total Reviews": "#fd79a8",
    "Average Score": "#ffcc00",
  };
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100">
        <div className="flex items-center mb-6">
          <FaChartPie className="text-blue-600 text-2xl mr-3" />
          <h2 className="text-2xl font-bold text-gray-800">KPI Data</h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Name
              </label>
              <input
                type="text"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                id="appName"
                placeholder="Enter Your App Name"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                required
              />
            </div>

            <div className="md:col-span-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country
              </label>
              <CountryList
                onSelect={(selectedCountry) => setCountry(selectedCountry)}
              />
            </div>

            <div className="md:col-span-2 flex items-end">
              <button
                type="submit"
                className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="mr-2" />
                    Loading...
                  </>
                ) : (
                  <>
                    <FaSearch className="mr-2" /> Search
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {loading && (
        <div className="flex justify-center items-center h-64">
          <Spinner animation="border" role="status" className="text-blue-600" />
          <span className="ml-3 text-lg text-gray-600 font-medium">Loading... Please Wait</span>
        </div>
      )}
      {error && <div className="alert alert-danger mt-3">{error}</div>}

      {appDetails && (
        <div className="mt-8">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 mb-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              KPI Data
            </h3>
            <div className="overflow-x-auto">
              <Table striped bordered hover responsive className="mb-0">
                <thead className="bg-gray-50">
                  <tr>
                    <th>Daily Active Users</th>
                    <th>Monthly Active Users</th>
                    <th>Retention Rate (%)</th>
                    <th>Churn Rate (%)</th>
                    <th>Avg Session Duration</th>
                    <th>User Acquisition Cost</th>
                    <th>Lifetime Value</th>
                    <th>Average Revenue Per User</th>
                    <th>Total Reviews (KPI)</th>
                    <th>Average Score</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{appDetails.DAU}</td>
                    <td>{appDetails.MAU}</td>
                    <td>{appDetails.retention_rate}</td>
                    <td>{appDetails.churn_rate}</td>
                    <td>{appDetails.avg_session_duration}</td>
                    <td>{appDetails.UAC}</td>
                    <td>{appDetails.LTV}</td>
                    <td>{appDetails.ARPU}</td>
                    <td>{appDetails.userRatingCount}</td>
                    <td>{appDetails.averageUserRating}</td>
                  </tr>
                </tbody>
              </Table>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mt-6 border border-gray-200">
              <h4 className="text-center text-lg font-bold text-gray-800 mb-4">Real-Time KPI Metrics</h4>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={data} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.5} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip contentStyle={{ background: "#f5f5f5", borderRadius: "8px" }} />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  
                  {Object.keys(lineColors).map((key) => (
                    <Line key={key} type="monotone" dataKey={key} stroke={lineColors[key]} strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {[
                { id: "dailyActiveUsersChart", title: "Daily Active Users", data: [{ name: "Daily Active Users", value: appDetails.DAU }], color: "#8884d8" },
                { id: "monthlyActiveUsersChart", title: "Monthly Active Users", data: [{ name: "Monthly Active Users", value: appDetails.MAU }], color: "#82ca9d" },
                { id: "retentionRateChart", title: "Retention Rate (%)", data: [{ name: "Retention Rate", value: appDetails.retention_rate }], color: "#ffc658" },
                { id: "churnRateChart", title: "Churn Rate (%)", data: [{ name: "Churn Rate", value: appDetails.churn_rate }], color: "#ff7f50" },
                { id: "avgSessionDurationChart", title: "Avg Session Duration (Minutes)", data: [{ name: "Avg Session Duration", value: appDetails.avg_session_duration }], color: "#8a2be2" },
                { id: "userAcquisitionCostChart", title: "User Acquisition Cost (UAC)", data: [{ name: "User Acquisition Cost", value: appDetails.UAC }], color: "#4caf50" },
                { id: "lifetimeValueChart", title: "Lifetime Value", data: [{ name: "Lifetime Value", value: appDetails.LTV }], color: "#f44336" },
                { id: "averageRevenuePerUserChart", title: "Average Revenue Per User", data: [{ name: "Average Revenue Per User", value: appDetails.ARPU }], color: "#3f51b5" },
                { id: "totalReviewsChart", title: "Total Reviews (KPI)", data: [{ name: "Total Reviews", value: appDetails.userRatingCount }], color: "#ff9800" },
                { id: "averageUserRatingChart", title: "Average User Rating", data: [{ name: "Average Rating", value: appDetails.averageUserRating }], color: "#673ab7" },
              ].map((chart, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-4 border border-gray-200" id={chart.id}>
                  <h5 className="text-center font-semibold mb-3">{chart.title}</h5>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={chart.data}>
                      <XAxis dataKey="name" hide />
                      <YAxis />
                      <Tooltip />
                      <CartesianGrid strokeDasharray="3 3" />
                      <Bar dataKey="value" fill={chart.color} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ))}
            </div>
          </div>

          {/* Download Buttons */}
          <div className="flex gap-4 justify-end">
            <button className="flex items-center bg-green-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors" onClick={downloadCSV}>
              <FaFileDownload className="mr-2" /> Download CSV
            </button>
            <button className="flex items-center bg-red-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-red-700 transition-colors" onClick={downloadPDF}>
              <FaFileDownload className="mr-2" /> Download PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPIData;
